import { useCallback, useEffect } from 'react';

import { FormField } from '@/_components';
import type { AdvanceFilter } from '@/_containers';
import { FilterApplyTrigger, FilterTypeSelector, getFilterKey, getFilterValue, useFiltersState } from '@/_containers';
import { CompanyMultiSelectCombo } from '@/_pages/companies/shared/components/company-multi-combo';
import { BrandsAsyncMultiSelect } from '@/_pages/settings/brands/shared';
import { PartnersMultiSelect } from '@/_pages/settings/partners/shared';
import { snackbar } from '@/_shared';

import { type InvoiceFilterSchema, useInvoiceFilterFormContext } from '../../shared/filters';

export function CustomFilterWithRadios({
  handleSubmitAdvFilters,
  ...perFilter
}: AdvanceFilter<InvoiceFilterSchema> & {
  handleSubmitAdvFilters: (v: AdvanceFilter<InvoiceFilterSchema>) => void;
}) {
  const { mainKey, subKey, subKey2, types, selectedType, applied } = perFilter;
  const form = useInvoiceFilterFormContext();

  const {
    typeState: [type, setType],
    transition: [isPending, startTransition],
    inputValueRequired,
  } = useFiltersState(null as string | null, types, selectedType);

  const isForm = mainKey === 'brand' || mainKey === 'partner' || mainKey === 'client';
  const isMultiFilter = mainKey === 'brand' || mainKey === 'partner' || mainKey === 'client';

  const handleSubmit = useCallback(() => {
    if (!isForm) {
      console.warn('not form field');
      return;
    }

    if (type === null || typeof type === 'undefined' || type === '') {
      snackbar.error('Please select a filter type');
      return;
    }

    const selectedType = types.find((f) => f.value === type) ?? { name: '', value: '' };
    const key = getFilterKey(mainKey, subKey, subKey2, selectedType?.absoluteKey) as keyof InvoiceFilterSchema;

    let specificKey = key;
    if (isMultiFilter) {
      specificKey = mainKey;
    }

    const specifValueToCheckOnly = form.getValues(specificKey) ?? '';

    if (
      (!specifValueToCheckOnly ||
        (isMultiFilter && Array.isArray(specifValueToCheckOnly) && specifValueToCheckOnly.length === 0)) &&
      inputValueRequired
    ) {
      snackbar.error('Please select a value');
      return;
    }

    let fromV: string | number = '';
    const formValueAll = form.getValues(mainKey as keyof InvoiceFilterSchema);
    const formValue = inputValueRequired ? (Array.isArray(formValueAll) ? formValueAll[0] : formValueAll) : '';

    if (subKey && typeof formValue !== 'string') {
      if (isMultiFilter && Array.isArray(formValueAll)) {
        fromV = formValueAll.map((item) => item[subKey as keyof typeof item]).join(', ');
      } else if (subKey2) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        fromV = (formValue?.[subKey as keyof typeof formValue]?.[subKey2 as keyof typeof formValue] ?? '').toString();
      } else {
        fromV = (formValue?.[subKey as keyof typeof formValue] ?? '').toString();
      }
    } else {
      fromV = (formValue ?? '').toString();
    }

    const { filter, filterValue } = getFilterValue(type, key, [fromV]);

    startTransition(() => {
      handleSubmitAdvFilters({
        ...perFilter,
        applied: true,
        filterValue,
        valueToApply: filter,
        selectedType,
      });
    });
  }, [
    isForm,
    type,
    types,
    mainKey,
    subKey,
    subKey2,
    form,
    inputValueRequired,
    startTransition,
    isMultiFilter,
    handleSubmitAdvFilters,
    perFilter,
  ]);

  useEffect(() => {
    if (!applied) {
      form.setValue(mainKey as keyof InvoiceFilterSchema, null as never);
      setType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  if (!isForm) {
    console.warn('not form field');
    return null;
  }

  return (
    <section className={'flex flex-col'}>
      {inputValueRequired && (
        <>
          {mainKey === 'brand' && (
            <FormField
              control={form.control}
              name="brand"
              render={({ field }) => (
                <BrandsAsyncMultiSelect
                  showSearchIcon
                  menuListClassName="max-h-28!"
                  menuPortalTarget={null}
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
          )}
          {mainKey === 'partner' && (
            <FormField
              control={form.control}
              name="partner"
              render={({ field }) => (
                <PartnersMultiSelect
                  showSearchIcon
                  menuListClassName="max-h-28!"
                  menuPortalTarget={null}
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
          )}
          {mainKey === 'client' && (
            <FormField
              control={form.control}
              name="client"
              render={({ field }) => (
                <CompanyMultiSelectCombo
                  showSearchIcon
                  menuListClassName="max-h-28!"
                  menuPortalTarget={null}
                  value={field.value}
                  onValueChange={field.onChange}
                />
              )}
            />
          )}
        </>
      )}

      <FilterTypeSelector
        className={inputValueRequired ? 'mt-0' : undefined}
        types={types}
        type={type}
        setType={setType}
      />

      <FilterApplyTrigger handleSubmit={handleSubmit} isPending={isPending} />
    </section>
  );
}
